import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import RatingStars from "./RatingStars";
import ContactPicker from "../contact/ContactPicker";
import AssetPicker from "../organization/AssetPicker";
import { Contact } from "../../types/User";
import { OrganizationAsset } from "../../types/Organization";
import {
    OrganizationRating,
    RatingContextType,
    RatingDimensionConfig,
    RatingDimensionScore,
    RatingType,
    categoryForContext,
} from "../../types/Rating";
import { fetchDimensionsByType } from "../../services/ratingService";
import { useConfirm } from "../../contexts/ConfirmContext";
import Button from "../Button";

interface RatingFormProps {
    organizationId: number;
    organizationName?: string;
    ratingType: RatingType;
    contextType?: RatingContextType;
    contextId?: number | null;
    contactId?: number | null;
    contactName?: string;
    /** Optional initial values when editing an existing rating. */
    initialRating?: Partial<OrganizationRating>;
    /** Start every dimension at this score (a rating Ask prepared from the person's words). */
    defaultScore?: number;
    /** Called when the form is submitted (parent handles the API call). */
    onSubmit: (rating: Partial<OrganizationRating>) => Promise<void> | void;
    onCancel?: () => void;
    isSubmitting?: boolean;
    title?: string;
    /** When true, show an optional ContactPicker so the user can tag a specific
     *  operator the rating is about (in addition to the org-level rating). */
    enableContactTag?: boolean;
    /** When true, show an optional AssetPicker so the user can tag a specific
     *  asset (vehicle/equipment) the rating is about. Provider-context only. */
    enableAssetTag?: boolean;
    /** Override the primary action button label (e.g. "Next", "Save & Finish"). */
    submitButtonLabel?: string;
    /** Override the secondary action button label (e.g. "Skip"). */
    cancelButtonLabel?: string;
    /** When true, allow submitting with no dimensions scored — the parent
     *  decides what to do with an empty rating (typically: treat as skip). */
    allowEmpty?: boolean;
    /** When true, suppress the form's own title + subtitle row (wizard parents
     *  render their own header so this avoids duplication). */
    hideHeader?: boolean;
    /** When true, suppress the form's footer (Submit/Cancel buttons). The
     *  parent must render its own submit trigger that targets `formId`. */
    hideFooter?: boolean;
    /** Optional form id so external buttons can submit via the HTML5 `form`
     *  attribute (`<button type="submit" form={formId}>`). */
    formId?: string;
}

/**
 * Multi-dimension rating input form. Loads active dimensions for the given rating type
 * from the backend (so labels reflect current admin configuration).
 */
const RatingForm: React.FC<RatingFormProps> = ({
    organizationId,
    organizationName,
    ratingType,
    contextType = RatingContextType.STANDALONE,
    contextId = null,
    contactId = null,
    contactName,
    initialRating,
    defaultScore,
    onSubmit,
    onCancel,
    isSubmitting = false,
    title,
    enableContactTag = false,
    enableAssetTag = false,
    submitButtonLabel,
    cancelButtonLabel,
    allowEmpty = false,
    hideHeader = false,
    hideFooter = false,
    formId,
}) => {
    const confirm = useConfirm();
    const [dimensions, setDimensions] = useState<RatingDimensionConfig[]>([]);
    const [scores, setScores] = useState<Record<number, number>>({});
    const [notes, setNotes] = useState<string>(initialRating?.notes ?? "");
    const [loading, setLoading] = useState(true);
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
    const [selectedAsset, setSelectedAsset] = useState<OrganizationAsset | null>(null);

    useEffect(() => {
        const loadDims = async () => {
            setLoading(true);
            // Filter dimensions to the category that matches the rating
            // context: invoice context → financial dimensions, everything
            // else (mission/standalone) → operational. Keeps each form
            // scoped to questions the rater can actually answer.
            const category = categoryForContext(contextType);
            const dims = await fetchDimensionsByType(ratingType, false, category);
            setDimensions(dims);

            // Pre-fill from initialRating if editing
            const initialScores: Record<number, number> = {};
            (initialRating?.ratingDetails ?? []).forEach((d: RatingDimensionScore) => {
                initialScores[d.dimensionConfigId] = d.score;
            });
            if (defaultScore && !Object.keys(initialScores).length) {
                dims.forEach((dimension) => { initialScores[dimension.ratingDimensionConfigId] = defaultScore; });
            }
            setScores(initialScores);

            // Pre-populate the contact/asset pickers from initialRating so editing
            // a tagged rating doesn't accidentally clear its tags on save.
            if (initialRating?.contactId) {
                setSelectedContact({
                    userDetailId: initialRating.contactId,
                    fullName: initialRating.contactName ?? "",
                    firstName: initialRating.contactName ?? "",
                    lastName: "",
                } as Contact);
            }
            if (initialRating?.assetId) {
                setSelectedAsset({
                    assetId: initialRating.assetId,
                    referenceNumber: initialRating.assetReferenceNumber ?? "",
                } as OrganizationAsset);
            }

            setLoading(false);
        };
        loadDims();
    }, [ratingType, contextType, initialRating]);

    const handleScoreChange = (dimensionConfigId: number, score: number) => {
        setScores((prev) => ({ ...prev, [dimensionConfigId]: score }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        // Stop the synthetic submit from bubbling to a parent <form> in the
        // React tree. RatingForm is often rendered inside a modal nested under
        // a page-level form (e.g. invoice/bill detail page), and without this
        // the parent form's onSubmit fires too — which would try to save the
        // host record (e.g. PUT a PAID bill, hitting "Only DRAFT can update").
        e.stopPropagation();
        const filledScores = Object.keys(scores).filter((id) => scores[Number(id)] > 0);
        if (filledScores.length === 0 && !allowEmpty) {
            toast.error("Please rate at least one dimension before submitting.");
            return;
        }
        // When allowEmpty is set (optional steps in the wizard), we let zero
        // scores through — the parent treats that as "skip without saving".
        if (filledScores.length > 0 && filledScores.length < dimensions.length) {
            const proceed = await confirm({
                entity: "Rating",
                titleVerb: "Submit",
                confirmLabel: "Submit",
                message: `You haven't rated all ${dimensions.length} dimensions. Submit anyway?`,
            });
            if (!proceed) return;
        }

        const ratingDetails: RatingDimensionScore[] = filledScores.map((id) => ({
            dimensionConfigId: Number(id),
            score: scores[Number(id)],
        }));

        // Compute a temporary overall (backend recomputes with weights)
        const overall = ratingDetails.reduce((sum, d) => sum + d.score, 0) / ratingDetails.length;

        // Resolve final contact/asset IDs:
        //   - If a tag picker is enabled, use the user's picker selection
        //   - Otherwise fall back to whatever the parent passed in (legacy behavior)
        const finalContactId =
            enableContactTag
                ? (selectedContact?.userDetailId ?? null)
                : (contactId ?? null);
        const finalAssetId =
            enableAssetTag && ratingType === RatingType.PROVIDER
                ? (selectedAsset?.assetId ?? null)
                : null;

        const payload: Partial<OrganizationRating> = {
            organizationRatingId: initialRating?.organizationRatingId ?? null,
            organizationId,
            contactId: finalContactId,
            assetId: finalAssetId,
            ratingType,
            contextType,
            contextId,
            overallRating: Math.round(overall * 100) / 100,
            ratingDetails,
            notes: notes.trim() === "" ? null : notes,
        };

        await onSubmit(payload);
    };

    if (loading) {
        return <div className="text-center p-3">Loading rating dimensions…</div>;
    }

    if (dimensions.length === 0) {
        return (
            <div className="alert alert-warning">
                No active rating dimensions configured for {ratingType}. Please ask an
                administrator to set them up before rating.
            </div>
        );
    }

    return (
        <form id={formId} onSubmit={handleSubmit}>
            {!hideHeader && title && <h5 className="mb-3">{title}</h5>}

            {!hideHeader && (
                <div className="mb-3">
                    <small className="text-muted">
                        Rating <strong>{organizationName ?? "organization"}</strong> as a{" "}
                        <strong>{ratingType}</strong>
                        {contactName && (
                            <>
                                {" "}— contact: <strong>{contactName}</strong>
                            </>
                        )}
                    </small>
                </div>
            )}

            <div className="rating-dimensions">
                {dimensions.map((dim) => (
                    <div key={dim.ratingDimensionConfigId} className="row mb-3 align-items-center">
                        <div className="col-md-5">
                            <label className="form-label mb-0 d-block">
                                {dim.label}
                            </label>
                            {dim.hint && (
                                <small className="d-block" style={{ fontSize: "var(--fs-xs)", color: "var(--color)", opacity: 0.7 }}>
                                    {dim.hint}
                                </small>
                            )}
                        </div>
                        <div className="col-md-5">
                            <RatingStars
                                value={scores[dim.ratingDimensionConfigId] ?? 0}
                                interactive
                                size={22}
                                onChange={(v) => handleScoreChange(dim.ratingDimensionConfigId, v)}
                            />
                        </div>
                        <div className="col-md-2 text-end">
                            {scores[dim.ratingDimensionConfigId] > 0 && (
                                <small style={{ color: "var(--header-color)" }}>
                                    {scores[dim.ratingDimensionConfigId]}/5
                                </small>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {(enableContactTag || (enableAssetTag && ratingType === RatingType.PROVIDER)) && (
                <div className="mt-3">
                    <small className="text-muted d-block mb-2">
                        Optional — tag a specific operator or asset this rating is about.
                        Their individual scores will roll up too.
                    </small>
                    <div className="row">
                        {enableContactTag && (
                            <div className="col-md-6">
                                <label className="form-label small">Operator (optional)</label>
                                <ContactPicker
                                    value={selectedContact}
                                    onChange={setSelectedContact}
                                    placeholder="Search operator..."
                                />
                            </div>
                        )}
                        {enableAssetTag && ratingType === RatingType.PROVIDER && (
                            <div className="col-md-6">
                                <label className="form-label small">Asset (optional)</label>
                                <AssetPicker
                                    value={selectedAsset}
                                    onChange={setSelectedAsset}
                                    organizationId={organizationId}
                                    placeholder="Select asset..."
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="mt-3">
                <label className="form-label">Notes (optional)</label>
                <textarea
                    className="form-control"
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Additional context, examples, or observations…"
                />
            </div>

            {!hideFooter && (
                <div className="d-flex justify-content-end mt-3" style={{ gap: 8 }}>
                    {onCancel && (
                        <button
                            type="button"
                            className="secondary-button"
                            onClick={onCancel}
                            disabled={isSubmitting}
                        >
                            {cancelButtonLabel ?? "Cancel"}
                        </button>
                    )}
                    <Button type="submit" variant="primary" disabled={isSubmitting}>
                        {isSubmitting
                            ? "Submitting…"
                            : submitButtonLabel ??
                              (initialRating?.organizationRatingId ? "Update" : "Submit")}
                    </Button>
                </div>
            )}
        </form>
    );
};

export default RatingForm;
